import { useMutation, useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";
import { menuItemsTypes } from "src/types/menu";

interface IProductRecipeBody {
  id?: string;
  branch_id: number | null;
  menu_id: number;
  items?: {
    quantity: number;
    item_id: number;
    methods: string[];
  }[];
  modifiers?: {
    quantity: number;
    unit_cost: number;
    menu_id: number;
  }[];
}

interface IProductBody {
  id?: number;
  name: string;
  sku: string;
  method: string;
  menu_category_id?: number;
  price: number;
  amount: number;
  type: string; //product
}

interface IParams {
  page?: number;
  name?: string;
  sku?: string;
  type?: string; // product | modifier
  menu_category_id?: number;
  method?: string;
}

interface ICatBody {
  id?: number;
  name: string;
}

// new
export const useGetMenuProducts = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-menu-products",
      params.page,
      params.name,
      params.sku,
      params.type,
      params.menu_category_id,
      params.method,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/menus`, { params });
    },
  });

export const useGetMenuProductById = ({
  id,
  enabled,
}: {
  id: string;
  enabled: boolean;
}) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-menu-product-by-id", id],
    queryFn: async () => {
      return apiClient.get(`/api/menus/${id}`);
    },
    gcTime: 0,
    enabled,
  });

export const useGetProductRecipeById = ({
  id,
  enabled,
}: {
  id: string | number;
  enabled: boolean;
}) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-menu-product-recipe-by-id", id],
    queryFn: async () => {
      return apiClient.get(`/api/menuRecipes/${id}`);
    },
    gcTime: 0,
    enabled,
  });

export const useDownloadIMenuProducts = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "download-items-list",
      params.type,
      params.name ?? "",
      params.sku,
      params.menu_category_id,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/menus/download`, { params });
    },
    enabled: false,
    gcTime: 0,
  });

export const useAddMenuProductMutation = () =>
  useMutation<void, APIErrorResponse, IProductBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/menus`, data);
    },
  });

export const useUpdateMenuProductMutation = () =>
  useMutation<void, APIErrorResponse, IProductBody>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/menus/${id}`, rest);
    },
  });

export const useDeleteMenuProductMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/menus/${id}`);
    },
  });

export const useGetDeletedMenuProducts = (params?: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-menu-deleted-products-list", params?.page],
    queryFn: async () => {
      return apiClient.get(`/api/menus/deleted-menus`, {
        params: params || {},
      });
    },
  });

export const useRestoreMenuProductMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.post(`/api/menus/restore-menu/${id}`);
    },
  });

export const useGetMenuCategories = (params?: { page?: number }) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-menu-categories", params?.page],
    queryFn: async () => {
      return apiClient.get(`/api/menuCategories`, { params: params || {} });
    },
  });

export const useAddCategoryMutation = () =>
  useMutation<void, APIErrorResponse, ICatBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/menuCategories`, data);
    },
  });

export const useUpdateCategoryMutation = () =>
  useMutation<void, APIErrorResponse, ICatBody>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/menuCategories/${id}`, rest);
    },
  });

export const useDeleteCategoryMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/menuCategories/${id}`);
    },
  });

export const useGetMenuSku = (params?: { type?: menuItemsTypes }) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-menu-next-sku", params?.type ?? ""],
    queryFn: async () => {
      return apiClient.get(`/api/menus/get-next-sku`, { params: params || {} });
    },
    gcTime: 0,
  });

// product recipe
export const useAddProductRecipeMutation = () =>
  useMutation<void, APIErrorResponse, IProductRecipeBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/menuRecipes`, data);
    },
  });

export const useUpdateProductRecipeMutation = () =>
  useMutation<void, APIErrorResponse, IProductRecipeBody>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/menuRecipes/${id}`, rest);
    },
  });

export const useImportMenuProductsMutation = () =>
  useMutation<any, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/menus/import`, data);
    },
  });

export const useImportMenuProductRecipeMutation = () =>
  useMutation<any, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/menuRecipes/import`, data);
    },
  });

export const useDownloadIMenuRecipes = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "download-menu-recipes",
      params.type,
      params.name ?? "",
      params.sku,
      params.menu_category_id,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/menuRecipes/download`, { params });
    },
    enabled: false,
    gcTime: 0,
  });
